// Tracing disabled — logs and metrics only. Uncomment the blocks below to re-enable.
// import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
// import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
// import type {
//   SpanProcessor,
//   Span,
//   ReadableSpan,
// } from '@opentelemetry/sdk-trace-base';

// import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
// import { ZoneContextManager } from '@opentelemetry/context-zone';
// import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
// import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction';
// import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
// import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { resourceFromAttributes } from '@opentelemetry/resources';
import {
  MeterProvider,
  PeriodicExportingMetricReader,
} from '@opentelemetry/sdk-metrics';
import { metrics } from '@opentelemetry/api';
// import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { logs } from '@opentelemetry/api-logs';
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http';
import {
  LoggerProvider,
  BatchLogRecordProcessor,
} from '@opentelemetry/sdk-logs';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { CONFIG } from 'config/environment_variables';
import { INSTRUMENTATION } from 'constant/instrumentation';
import { getServerName } from 'utils/functions/server';
import { getOSInfo, getBrowserInfo } from 'utils/platform';
import { ServerName } from 'types/config';
import {
  startLongTaskMonitor,
  startGlobalErrorMonitor,
} from 'utils/observability/monitor';
// import { getHeapSize } from 'utils/observability/storage';
// import { getWindowLocationPathname } from 'utils/functions/window';

const signozBaseUrl = CONFIG.SIGNOZ_URL;
const signozIngestionKey = CONFIG.SIGNOZ_INGESTION_KEY;

// Matches any single character that has special meaning in regex.
// const escapeRegex = (value: string) =>
//   value?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const signozBase = signozBaseUrl?.replace(/\/+$/, '');

// Used to exclude SigNoz ingest calls from being traced — avoids infinite
// loops where the exporter's own HTTP requests generate new spans.
// const signozIngestRegex = new RegExp(
//   `^${escapeRegex(signozBase)}/v1/(traces|metrics|logs)(\\?.*)?$`,
// );

// Allowlist for W3C trace-context header injection. Only propagate to our own
// backend — injecting into third-party URLs (Google, Stripe, etc.) leaks
// internal tracing metadata and can cause CORS preflight failures.
// const backendRegex = new RegExp(`^${escapeRegex(CONFIG.API_URL)}`);

// Computed once at module load — os.name does not change during a session.
// Used by DynamicAttributeSpanProcessor to stamp every span so SigNoz's
// trace query builder (which reads span attributes, not resource attributes)
// can reliably GROUP BY os.name.
// const osName = getOSInfo();

// ---------------------------------------------------------------------------
// Dynamic attribute span processor
//
// Problem: resource attributes (below) are frozen at init time. In a typical
// SPA auth flow the app loads → instrumentation runs → user logs in. All
// spans created before and during login would carry 'UnknownUser' from the
// resource if user.id and tenant.id were only read there.
//
// Solution: a lightweight SpanProcessor that reads user.id and tenant.id from
// localStorage on every span start. localStorage is always up-to-date because
// the auth flow writes there before any authenticated API call is made.
//
// These span-level attributes override the stale resource-level values when
// SigNoz indexes the data, giving accurate per-span identity even for spans
// created in the first seconds after login.
//
// If you add new per-session attributes that change after login (e.g. role,
// org), add them here rather than to the resource below.
// ---------------------------------------------------------------------------
// class DynamicAttributeSpanProcessor implements SpanProcessor {
//   onStart(span: Span): void {
//     span.setAttribute(
//       'user.id',
//       localStorage.getItem(LOCAL_STORAGE_KEYS.USER_ID) ?? 'UnknownUser',
//     );
//     span.setAttribute(
//       'tenant.id',
//       localStorage.getItem(LOCAL_STORAGE_KEYS.TENANT) ?? 'UnknownTenant',
//     );
//     span.setAttribute(
//       'tenant.name',
//       sessionStorage.getItem(SESSION_STORAGE_KEYS.TENANT_NAME) ??
//         'UnknownTenant',
//     );
//     span.setAttribute(
//       'user.email',
//       sessionStorage.getItem(SESSION_STORAGE_KEYS.EMAIL) ?? 'UnknownEmail',
//     );

//     // os.name is set in the resource (frozen at init), but SigNoz trace queries
//     // read span attributes for GROUP BY and count_distinct operations. Stamping
//     // it here as a span attribute ensures consistent, queryable data on every
//     // span — the resource value does not change per-session so reading it once
//     // at processor instantiation is safe and avoids the getOSInfo() call overhead
//     // on every span.
//     span.setAttribute('os.name', osName);

//     // screen.name in the resource is frozen to the pathname at initialization.
//     // In a SPA every navigation changes window.location.pathname without a page
//     // reload, so the resource value goes stale after the first route change.
//     // Reading it live here stamps the actual current route on every span.
//     span.setAttribute('screen.name', getWindowLocationPathname());

//     // Heap memory changes continuously as the app allocates and GC runs —
//     // reading at span creation time captures the pressure at the moment of the
//     // event rather than the (stale) value from app startup.
//     const heapSize = getHeapSize();
//     span.setAttribute('memory.jsHeapSizeLimit', heapSize.jsHeapSizeLimit);
//     span.setAttribute('memory.totalJSHeapSize', heapSize.totalJSHeapSize);
//     span.setAttribute('memory.usedJSHeapSize', heapSize.usedJSHeapSize);
//   }

//   // onEnd is called after the span finishes — attributes are locked at this
//   // point so we only write in onStart.
//   onEnd(_span: ReadableSpan): void {}

//   forceFlush(): Promise<void> {
//     return Promise.resolve();
//   }

//   shutdown(): Promise<void> {
//     return Promise.resolve();
//   }
// }

// const traceExporter = new OTLPTraceExporter({
//   url: `${signozBase}/v1/traces`,
//   headers: {
//     // Required for SigNoz Cloud; optional for self-hosted.
//     'signoz-ingestion-key': signozIngestionKey,
//   },
// });

// Resource holds stable, service-level attributes that apply to ALL telemetry
// (traces, metrics, logs) from this browser session. These are set once at
// init and do not change for the lifetime of the page.
//
// Note: user.id and tenant.id are intentionally kept here as fallback values.
// The DynamicAttributeSpanProcessor overrides them per-span with live values,
// so traces always reflect the currently logged-in user even if login happened
// after instrumentation initialized.
const resource = resourceFromAttributes({
  'service.name': INSTRUMENTATION.SERVICE_NAME,
  'app.build_version': __APP_BUILD_VERSION__,
  'deployment.environment': getServerName() as ServerName,
  'browser.name': getBrowserInfo(),
  'browser.user_agent': navigator.userAgent,
  'os.name': getOSInfo(),
  // user.id, user.email, tenant.id, tenant.name are intentionally omitted here.
  // The resource is frozen at page load before the user has authenticated, so these would always be 'UnknownUser'/'UnknownTenant'. They are stamped live on every log record by getLiveUserAttrs() in utils/observability/logger.ts.

  // TODO(signoz-cleanup): Remove — resource-level user/tenant attrs superseded by getLiveUserAttrs() in utils/observability/logger.ts
  // Snapshot at init — overridden per-span by DynamicAttributeSpanProcessor.
  // 'tenant.id':
  //   localStorage.getItem(LOCAL_STORAGE_KEYS.TENANT) ?? 'UnknownTenant',
  // 'tenant.name':
  //   sessionStorage.getItem(SESSION_STORAGE_KEYS.TENANT_NAME) ?? 'UnknownTenant',
  // 'user.id': localStorage.getItem(LOCAL_STORAGE_KEYS.USER_ID) ?? 'UnknownUser',
  // 'user.email':
  //   sessionStorage.getItem(SESSION_STORAGE_KEYS.EMAIL) ?? 'UnknownEmail',
  'browser.language': navigator.language,
  'device.type': /Mobile|Android|iPhone/i.test(navigator.userAgent)
    ? 'mobile'
    : 'desktop',
  'screen.width': window?.screen?.width ?? '0',
  'screen.height': window?.screen?.height ?? '0',
  'viewport.width': window?.innerWidth ?? '0',
  'viewport.height': window?.innerHeight ?? '0',
  // screen.name and memory.* are intentionally omitted here — resource attributes
  // are frozen at page load. Those values change with every navigation / GC cycle
  // and are stamped as span attributes by DynamicAttributeSpanProcessor.onStart()
  // instead, where they are read live on every span creation.
  'browser.version': navigator.appVersion,
});

// const traceProvider = new WebTracerProvider({
//   resource: resource,
//   spanProcessors: [
//     // DynamicAttributeSpanProcessor must come first so user.id / tenant.id
//     // are stamped before BatchSpanProcessor enqueues the span for export.
//     new DynamicAttributeSpanProcessor(),
//     new BatchSpanProcessor(traceExporter, {
//       scheduledDelayMillis: 30000, // flush every 30s — balanced between network overhead and data loss on tab close
//       maxExportBatchSize: 512,
//       maxQueueSize: 2048, // silently drops spans beyond this without error; raise if high-traffic pages generate many spans
//     }),
//   ],
// });

const loggerProvider = new LoggerProvider({
  resource: resource,
  processors: [
    new BatchLogRecordProcessor(
      new OTLPLogExporter({
        url: `${signozBase}/v1/logs`,
        headers: {
          'signoz-ingestion-key': signozIngestionKey,
        },
      }),
      {
        // Default is 1000ms — causes a /v1/logs HTTP call every second when any log is queued.
        // 30s matches the trace processor interval; logs are low-frequency so data loss risk is minimal.
        // scheduledDelayMillis: 30000,
        maxExportBatchSize: 512,
        maxQueueSize: 2048,
      },
    ),
  ],
});

const metricReader = new PeriodicExportingMetricReader({
  exporter: new OTLPMetricExporter({
    url: `${signozBase}/v1/metrics`,
    headers: {
      'signoz-ingestion-key': signozIngestionKey,
    },
  }),
  exportIntervalMillis: 5 * 60_000, // Export metrics every 5 minutes (default is 10 seconds)
});

const myServiceMeterProvider = new MeterProvider({
  resource,
  readers: [metricReader],
});

// Guard against double-registration in React Strict Mode or HMR. Without this,
// each hot-reload re-runs traceProvider.register() and setGlobal* calls,
// creating a new BatchSpanProcessor with a fresh 30s timer on every save.
// Old processors keep their timers running, so after N HMR cycles there are N
// concurrent export requests all stalling the browser's connection pool.
if (!window.__otelInitialized && signozBaseUrl && signozIngestionKey) {
  window.__otelInitialized = true;
  metrics.setGlobalMeterProvider(myServiceMeterProvider);
  logs.setGlobalLoggerProvider(loggerProvider);
  // traceProvider.register({
  //   contextManager: new ZoneContextManager(),
  // });

  // Flush all buffered telemetry when the tab is hidden (most reliable signal
  // for tab close / navigate-away). Without this, up to 30s of traces and logs
  // are silently dropped when users close or switch tabs before the next
  // scheduled flush.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      // traceProvider.forceFlush();
      loggerProvider.forceFlush();
      myServiceMeterProvider.forceFlush();
    }
  });

  window.addEventListener('error', () => {
    // traceProvider.forceFlush();
    loggerProvider.forceFlush();
    myServiceMeterProvider.forceFlush();
  });

  window.addEventListener('unhandledrejection', () => {
    // traceProvider.forceFlush();
    loggerProvider.forceFlush();
    myServiceMeterProvider.forceFlush();
  });

  startLongTaskMonitor();
  startGlobalErrorMonitor();
  // registerInstrumentations({
  //   instrumentations: [
  //     new FetchInstrumentation({
  //       ignoreUrls: [signozIngestRegex],
  //       propagateTraceHeaderCorsUrls: [backendRegex],
  //       // Suppress internal browser network events (DNS, TCP, TLS) from spans
  //       // to keep trace payloads lean. Set to false if you need to debug
  //       // network-level latency.
  //       ignoreNetworkEvents: true,
  //     }),
  //     new UserInteractionInstrumentation({
  //       eventNames: [
  //         'click',
  //         // 'input', — omitted: fires on every keystroke and produces too
  //         //            many low-value spans; re-enable only for form auditing.
  //         'submit',
  //       ],
  //     }),
  //     new XMLHttpRequestInstrumentation({
  //       // Skip creating spans for SigNoz ingest calls (same reason as Fetch above).
  //       ignoreUrls: [signozIngestRegex],
  //       // Same allowlist approach as FetchInstrumentation — only inject W3C
  //       // trace-context headers into requests going to our own API.
  //       propagateTraceHeaderCorsUrls: [backendRegex],
  //     }),
  //     new DocumentLoadInstrumentation(),
  //   ],
  // });
}
